import { useSyncExternalStore } from 'react';
import { recordingMediaKey } from '../../utils/recordingMediaUrl';
import { storage } from '../../utils/storage';

/**
 * CORE-1383 (возврат QA 06.10) — вердикт «в записи только звук», добытый плеером.
 *
 * Бэкенд отдаёт `hasVideo`, но до замера файла в нём `null`, и на staging
 * замер не пришёл ни через восемь минут, ни позже. Плеер при этом правду знал:
 * на `loadedmetadata` кадр оказался 0 × 0 (CORE-1573), и он показал «Только
 * звук». Карточка архива о том же файле молчала и рисовала камеру — две
 * поверхности расходились на одной записи.
 *
 * Здесь вердикт плеера запоминается по адресу записи, и карточка читает его
 * как второй источник после флага бэкенда. Холодная загрузка архива при
 * `null` по-прежнему молчит: загружать плейлист каждой карточки ради пометки
 * — не путь, а другого источника у клиента нет.
 *
 * Обычный модуль без провайдера, как `cookieBannerInset`: писатель один
 * (плеер), читателей мало. Переживает перезагрузку через `storage`, чтобы
 * «вернулся из плеера, обновил страницу» не стирало уже увиденное.
 */

/** Чтобы хранилище не росло вечно: столько записей мастер и так не пересмотрит. */
const MAX_REMEMBERED = 200;

let remembered: string[] = [];
const listeners = new Set<() => void>();

/**
 * Ключ записи — путь файла без хоста и подписи. Плеер и архив получают адрес
 * из разных ответов бэкенда; подписанный параметр или другой домен CDN не
 * должны делать из одной записи две.
 */
export function audioOnlyKey(playbackUrl: string | null | undefined): string | null {
  // CORE-1919 — адрес API со сроком сводится к пути объекта, а не к пути с подписью.
  return recordingMediaKey(playbackUrl);
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

function commit(next: string[]): void {
  remembered = next;
  notify();
  // Персист — в фоне и без ожидания: вердикт уже у читателей, а отказ
  // хранилища (приватный режим, квота) не повод ломать плеер.
  storage.setAudioOnlyRecordings(next).catch(() => {});
}

/**
 * Плеер сообщает, что увидел на `loadedmetadata`: `audioOnly = true` — кадр
 * 0 × 0, запомнить; `false` — кадр есть, и прежний вердикт по этому адресу
 * (если был) снимается.
 */
export function recordAudioOnlyProbe(playbackUrl: string | null | undefined, audioOnly: boolean): void {
  const key = audioOnlyKey(playbackUrl);
  if (!key) return;
  const has = remembered.includes(key);
  if (audioOnly && !has) {
    commit([...remembered, key].slice(-MAX_REMEMBERED));
  } else if (!audioOnly && has) {
    commit(remembered.filter((k) => k !== key));
  }
}

export function isProbedAudioOnly(playbackUrl: string | null | undefined): boolean {
  const key = audioOnlyKey(playbackUrl);
  return key != null && remembered.includes(key);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Что плеер уже узнал об этой записи; `false` — ничего или кадр был. */
export function useProbedAudioOnly(playbackUrl: string | null | undefined): boolean {
  const snapshot = useSyncExternalStore(subscribe, () => remembered, () => remembered);
  const key = audioOnlyKey(playbackUrl);
  return key != null && snapshot.includes(key);
}

let hydration: Promise<void> | null = null;

/** Поднять запомненное из хранилища. Один раз на процесс; повторный вызов — та же промис-обёртка. */
export function hydrateAudioOnlyRecordings(): Promise<void> {
  if (!hydration) {
    hydration = storage.getAudioOnlyRecordings()
      .then((stored) => {
        if (stored.length === 0) return;
        // Увиденное в этой сессии до окончания чтения — свежее сохранённого.
        const merged = [...stored.filter((k) => !remembered.includes(k)), ...remembered];
        remembered = merged.slice(-MAX_REMEMBERED);
        notify();
      })
      .catch(() => {});
  }
  return hydration;
}

/** Только для тестов. */
export function resetAudioOnlyRecordingsForTests(): void {
  remembered = [];
  hydration = null;
  listeners.clear();
}

hydrateAudioOnlyRecordings();
